import { collectPagebreakMarkers } from '../PagebreakTracker.js';

/**
 * Dev-only pagebreak label overlay (pagebreaks=1 knob).
 *
 * The dashed rule stays pure CSS on the marker itself: it is inline, so it marks the break
 * without the false line wrap a block-level marker forced mid-paragraph. The badge is a real
 * element injected as a child of that marker, positioned absolutely against it, so it hangs
 * under the rule and overlays the text rather than taking inline space and pushing the words
 * apart. Being out of flow also keeps it out of the marker's own rect, so the tracker reads
 * the same geometry whether the overlay is on or off.
 *
 * Injected once per frame and never repainted: the badge is a child of the marker, so the
 * layout engine carries it through page turns and reflows. "Once per frame" is not the same
 * as "on frameLoaded" though — that event fires only for `currentFrames[0]`, so the caller
 * has to sweep the live frames rather than trust it (see EpubViewer._renderPagebreakLabels).
 *
 * What CSS cannot do is pick the side. An out-of-flow box resolves to its static position
 * with no way to know it is about to run off the column, so a break near the right edge
 * rendered a clipped badge. CSS anchor positioning is the feature for exactly that and it
 * does not engage here — the implicit pseudo-element anchor never resolves, so
 * `position-area` is ignored (checked in Chrome 148, inside and outside a multi-column
 * container, with inline and inline-block markers: the badge stayed put in all four).
 *
 * So JS owns one decision and nothing else: which side. It is decided against the edge of the
 * marker's OWN column, not the viewport edge — a two-column spread puts two columns in one
 * viewport, so measuring against the viewport only catches breaks near the outer edge and
 * lets the ones at the inner edge spill into the gutter.
 *
 * Markers come from PagebreakTracker's own collector, so the overlay marks exactly the
 * elements the tracker counts. Badges need no teardown — the frame document owns them and
 * takes them with it when Readium recycles it.
 */

/** Matches the classes the debug stylesheet styles. */
const LABEL_CLASS = 'volpe-pagebreak-label';
const FLIP_CLASS = 'volpe-pagebreak-label--flip';

/** Breathing room kept between the badge and the column edge, in px. */
const EDGE_GAP_PX = 4;

/** Same precedence as PagebreakTracker's extraction: aria-label wins over title. */
function labelTextFor(marker) {
    return `p. ${marker.getAttribute('aria-label') || marker.getAttribute('title') || '?'}`;
}

/**
 * The right edge of the column the marker sits in.
 *
 * Measured rather than derived from Readium's column settings: a block fragmented across
 * columns reports one client rect per fragment, so the fragment horizontally containing the
 * marker already describes its column. That holds for any column count, gutter or zoom, and
 * degrades to the single content rect in scroll mode. Falls back to the viewport edge when
 * the marker has no block parent to measure.
 */
function columnRightEdgeFor(marker, markerRect, frameWindow) {
    const fragments = marker.parentElement?.getClientRects?.() ?? [];

    for (const fragment of fragments) {
        if (markerRect.left >= fragment.left - 1 && markerRect.left <= fragment.right + 1) {
            return fragment.right;
        }
    }

    return frameWindow.innerWidth;
}

/**
 * Injects the badges for one chapter frame. Idempotent, so a caller that fires twice for the
 * same frame (a reloaded chapter, a retried frame) costs one query and nothing else.
 *
 * @param {Window} frameWindow - the chapter iframe's window, as handed to `frameLoaded`
 */
export function renderPagebreakLabels(frameWindow) {
    const doc = frameWindow?.document;
    if (!doc?.body) return;

    for (const marker of collectPagebreakMarkers(doc)) {
        try {
            if (marker.querySelector(`.${LABEL_CLASS}`)) continue;

            const label = doc.createElement('span');
            label.className = LABEL_CLASS;
            label.textContent = labelTextFor(marker);
            marker.appendChild(label);

            // Width has to be read after the badge is in the DOM — the side depends on it.
            const markerRect = marker.getBoundingClientRect();
            const columnRight = columnRightEdgeFor(marker, markerRect, frameWindow);
            if (markerRect.left + label.offsetWidth + EDGE_GAP_PX > columnRight) {
                label.classList.add(FLIP_CLASS);
            }
        } catch (_e) {
            // A void element (a <br> carrying the role) rejects children, and a torn-down
            // frame throws on access. Neither is worth reporting from a debug overlay.
        }
    }
}
